import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Users, Info, Phone, BarChart3, Star, ChevronDown, FolderOpen, ChevronRight, Home } from "lucide-react";
import Logo from "./Logo";
import { useModal } from "../contexts/ModalContext";

const API_URL = process.env.REACT_APP_API_URL;

function Navbar({ token }) {
  const { openAgentsModal } = useModal();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [categories, setCategories] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showMobileCategoryDropdown, setShowMobileCategoryDropdown] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [expandedMobileCategory, setExpandedMobileCategory] = useState(null);

  const isLoggedIn = Boolean(token);


  // Fetch categories with subcategories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/api/categories?active=true&includeSubcategories=true`);
        if (response.ok) {
          const data = await response.json();
          // Filter only main categories (those without parent)
          const mainCategories = data.filter(cat => !cat.parent);
          setCategories(mainCategories);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCategoryDropdown && !event.target.closest('.category-dropdown-container')) {
        setShowCategoryDropdown(false);
        setExpandedCategory(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCategoryDropdown]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollThreshold = 50;

      setIsScrolled(currentScrollY > 10);

      if (currentScrollY <= 10) {
        setIsHidden(false);
        setLastScrollY(currentScrollY);
        return;
      }

      if (Math.abs(currentScrollY - lastScrollY) < scrollThreshold) {
        return;
      }

      if (currentScrollY > lastScrollY) {
        setIsHidden(true);
      } else {
        setIsHidden(false);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const handleCategorySelect = (categoryName) => {
    // Always store selection in sessionStorage for cross-page navigation
    sessionStorage.setItem("pendingCategorySelection", categoryName);

    if (location.pathname === "/") {
      // If already on Home, trigger the update via custom event
      const event = new CustomEvent("navbar-category-change", {
        detail: { categoryName }
      });
      window.dispatchEvent(event);
      setShowCategoryDropdown(false);
      setShowMobileCategoryDropdown(false);
      setIsMobileMenuOpen(false);
    } else {
      // If on another page, navigate to Home (Home will pick up from sessionStorage)
      navigate("/");
      setShowCategoryDropdown(false);
      setShowMobileCategoryDropdown(false);
      setIsMobileMenuOpen(false);
    }
  };

  const navLinks = [
    { to: "/", label: "Home", icon: Home },
    { to: "/review", label: "Review", icon: Star },
    { to: "/about", label: "About Us", icon: Info },
    { to: "/contact", label: "Contact Us", icon: Phone },
    ...(isLoggedIn ? [{ to: "/dashboard", label: "Dashboard", icon: BarChart3 }] : [])
  ];

  return (
    <div className={`fixed top-0 left-0 right-0 z-50 transition-transform duration-300 ease-in-out ${isHidden ? '-translate-y-full' : 'translate-y-0'}`}>
      <nav
        className={`w-full transition-all duration-500 ${isScrolled
          ? "bg-white/30 backdrop-blur-xl shadow-2xl border-b border-white/20"
          : "bg-gradient-to-r from-orange-500 via-orange-600 to-amber-500 backdrop-blur-xl shadow-lg"
          }`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo Section */}
            <div className="flex items-center flex-shrink-0">
              <Link
                to="/"
                className="group flex items-center space-x-3 transform transition-transform duration-300 hover:scale-105"
              >
                <Logo className={isScrolled ? "text-orange-500" : "text-white"} />
                <div className="block">
                  <h1 className={`text-lg lg:text-2xl font-black leading-tight transition-colors duration-300 ${isScrolled ? "text-gray-900" : "text-white"}`}>
                    Quick Runner
                  </h1>
                  <p className={`text-[10px] lg:text-xs transition-colors duration-300 ${isScrolled ? "text-orange-600" : "text-orange-100"}`}>
                    Fashion & Style
                  </p>
                </div>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {navLinks.map((link, index) => {
                const isActive = location.pathname === link.to;

                if (index === 0) {
                  return (
                    <React.Fragment key={link.to}>
                      <Link
                        to={link.to}
                        className={`group relative px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 ${isActive
                          ? isScrolled
                            ? "bg-white/20 backdrop-blur-lg text-gray-900 font-bold shadow-lg"
                            : "bg-white/30 backdrop-blur-md text-gray-900 font-bold shadow-lg"
                          : isScrolled
                            ? "text-gray-700 hover:text-orange-600 hover:bg-orange-50"
                            : "text-white hover:bg-white/20"
                          }`}
                      >
                        <div className="flex items-center space-x-2">
                          <link.icon className="w-4 h-4" />
                          <span>{link.label}</span>
                        </div>
                      </Link>

                      {/* Find Agents Button */}
                      <button
                        onClick={openAgentsModal}
                        className={`group relative px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 flex items-center space-x-2 ${isScrolled
                          ? "text-gray-700 hover:text-orange-600 hover:bg-orange-50"
                          : "text-white hover:bg-white/20"
                          }`}
                      >
                        <Users className="w-4 h-4" />
                        <span>Find Agents</span>
                      </button>
                    </React.Fragment>
                  );
                }

                if (link.label === "Review") {
                  return (
                    <React.Fragment key="category-review-group">
                      {/* Categories Dropdown for Desktop */}
                      <div className="relative category-dropdown-container">
                        <button
                          onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                          className={`group relative px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 flex items-center space-x-2 ${isScrolled
                            ? "text-gray-700 hover:text-orange-600 hover:bg-orange-50"
                            : "text-white hover:bg-white/20"
                            }`}
                        >
                          <FolderOpen className="w-4 h-4" />
                          <span>Categories</span>
                          <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${showCategoryDropdown ? 'rotate-180' : ''}`} />
                        </button>

                        {showCategoryDropdown && categories.length > 0 && (
                          <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-50 max-h-96 overflow-y-auto">
                            {categories.map((category) => (
                              <div key={category._id} className="px-2">
                                <div className="flex items-center justify-between">
                                  <button
                                    onClick={() => handleCategorySelect(category.name)}
                                    className="flex-1 text-left px-3 py-2 text-sm font-semibold text-gray-900 hover:bg-orange-50 rounded-lg transition-colors"
                                  >
                                    {category.name}
                                  </button>
                                  {category.subcategories && category.subcategories.length > 0 && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedCategory(expandedCategory === category._id ? null : category._id);
                                      }}
                                      className="p-2 hover:bg-orange-50 rounded-lg transition-colors"
                                    >
                                      <ChevronRight className={`w-4 h-4 text-gray-600 transition-transform duration-200 ${expandedCategory === category._id ? 'rotate-90' : ''}`} />
                                    </button>
                                  )}
                                </div>
                                {expandedCategory === category._id && category.subcategories && category.subcategories.length > 0 && (
                                  <div className="ml-4 mb-2 mt-1 space-y-1">
                                    {category.subcategories.map((subcat) => (
                                      <button
                                        key={subcat._id}
                                        onClick={() => handleCategorySelect(subcat.name)}
                                        className="block w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-orange-50 hover:text-orange-600 rounded-md transition-colors"
                                      >
                                        • {subcat.name}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <Link
                        to={link.to}
                        className={`group relative px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 ${isActive
                          ? isScrolled
                            ? "bg-white/20 backdrop-blur-lg text-gray-900 font-bold shadow-lg"
                            : "bg-white/30 backdrop-blur-md text-gray-900 font-bold shadow-lg"
                          : isScrolled
                            ? "text-gray-700 hover:text-orange-600 hover:bg-orange-50"
                            : "text-white hover:bg-white/20"
                          }`}
                      >
                        <div className="flex items-center space-x-2">
                          <link.icon className="w-4 h-4" />
                          <span>{link.label}</span>
                        </div>
                      </Link>
                    </React.Fragment>
                  );
                }

                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`group relative px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 ${isActive
                      ? isScrolled
                        ? "bg-white/20 backdrop-blur-lg text-gray-900 font-bold shadow-lg"
                        : "bg-white/30 backdrop-blur-md text-gray-900 font-bold shadow-lg"
                      : isScrolled
                        ? "text-gray-700 hover:text-orange-600 hover:bg-orange-50"
                        : "text-white hover:bg-white/20"
                      }`}
                  >
                    <div className="flex items-center space-x-2">
                      <link.icon className="w-4 h-4" />
                      <span>{link.label}</span>
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`relative p-3 rounded-xl transition-all duration-300 ${isScrolled
                  ? "bg-white/30 backdrop-blur-lg text-orange-600 hover:bg-white/40"
                  : "bg-white/30 text-white hover:bg-white/40 backdrop-blur-md"
                  }`}
              >
                <div className="w-6 h-6 relative">
                  <span className={`absolute block w-6 h-0.5 transition-all duration-300 ${isMobileMenuOpen ? "rotate-45 top-3" : "top-1"} ${isScrolled ? "bg-orange-600" : "bg-white"}`}></span>
                  <span className={`absolute block w-6 h-0.5 transition-all duration-300 top-3 ${isMobileMenuOpen ? "opacity-0" : "opacity-100"} ${isScrolled ? "bg-orange-600" : "bg-white"}`}></span>
                  <span className={`absolute block w-6 h-0.5 transition-all duration-300 ${isMobileMenuOpen ? "-rotate-45 top-3" : "top-5"} ${isScrolled ? "bg-orange-600" : "bg-white"}`}></span>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden transition-all duration-500 overflow-hidden ${isMobileMenuOpen ? "max-h-[80vh] opacity-100" : "max-h-0 opacity-0"} ${isScrolled ? 'bg-white' : 'bg-orange-600'}`}>
          <div className="px-4 py-6 space-y-3 overflow-y-auto max-h-[70vh]">
            {navLinks.map((link, index) => {
              const isActive = location.pathname === link.to;

              if (index === 0) {
                return (
                  <React.Fragment key={link.to}>
                    <Link
                      to={link.to}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`block px-4 py-3 rounded-xl text-base font-semibold transition-all duration-300 ${isActive
                        ? isScrolled
                          ? "bg-orange-50 text-orange-600 font-bold"
                          : "bg-white/20 text-white font-bold"
                        : isScrolled ? "text-gray-900 hover:bg-orange-50" : "text-white hover:bg-white/10"
                        }`}
                    >
                      <div className="flex items-center space-x-3">
                        <link.icon className={`w-5 h-5 ${isActive && isScrolled ? 'text-orange-500' : ''}`} />
                        <span>{link.label}</span>
                      </div>
                    </Link>

                    {/* Find Agents Button */}
                    <button
                      onClick={() => {
                        openAgentsModal();
                        setIsMobileMenuOpen(false);
                      }}
                      className={`w-full px-4 py-3 rounded-xl text-base font-semibold flex items-center space-x-3 transition-all duration-300 ${isScrolled ? "text-gray-900 border border-gray-200" : "text-white bg-white/10"}`}
                    >
                      <Users className="w-5 h-5" />
                      <span>Find Agents</span>
                    </button>
                  </React.Fragment>
                );
              }

              if (link.label === "Review") {
                return (
                  <React.Fragment key="mobile-category-review">
                    {/* Categories for Mobile */}
                    <div className="space-y-2">
                      <button
                        onClick={() => setShowMobileCategoryDropdown(!showMobileCategoryDropdown)}
                        className={`w-full px-4 py-3 rounded-xl text-base font-semibold flex items-center justify-between ${isScrolled ? "text-gray-900 border border-gray-200" : "text-white bg-white/10"}`}
                      >
                        <div className="flex items-center space-x-3">
                          <FolderOpen className="w-5 h-5" />
                          <span>Categories</span>
                        </div>
                        <ChevronDown className={`w-4 h-4 transition-transform ${showMobileCategoryDropdown ? 'rotate-180' : ''}`} />
                      </button>

                      {showMobileCategoryDropdown && categories.map((category) => (
                        <div key={category._id} className="ml-4 space-y-1">
                          <div className="flex items-center justify-between">
                            <button
                              onClick={() => handleCategorySelect(category.name)}
                              className={`flex-1 text-left px-4 py-2 text-sm font-semibold ${isScrolled ? "text-gray-700" : "text-orange-100"}`}
                            >
                              {category.name}
                            </button>
                            {category.subcategories?.length > 0 && (
                              <button onClick={() => setExpandedMobileCategory(expandedMobileCategory === category._id ? null : category._id)} className="p-2">
                                <ChevronRight className={`w-4 h-4 transition-transform ${expandedMobileCategory === category._id ? 'rotate-90' : ''} ${isScrolled ? 'text-gray-400' : 'text-orange-200'}`} />
                              </button>
                            )}
                          </div>
                          {expandedMobileCategory === category._id && category.subcategories?.map((sub) => (
                            <button
                              key={sub._id}
                              onClick={() => handleCategorySelect(sub.name)}
                              className={`block w-full text-left px-8 py-2 text-sm ${isScrolled ? "text-gray-600" : "text-orange-200"}`}
                            >
                              • {sub.name}
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>

                    <Link
                      to={link.to}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`block px-4 py-3 rounded-xl text-base font-semibold transition-all duration-300 ${isActive
                        ? isScrolled
                          ? "bg-orange-50 text-orange-600 font-bold"
                          : "bg-white/20 text-white font-bold"
                        : isScrolled ? "text-gray-900 hover:bg-orange-50" : "text-white hover:bg-white/10"
                        }`}
                    >
                      <div className="flex items-center space-x-3">
                        <link.icon className={`w-5 h-5 ${isActive && isScrolled ? 'text-orange-500' : ''}`} />
                        <span>{link.label}</span>
                      </div>
                    </Link>
                  </React.Fragment>
                );
              }

              return (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-4 py-3 rounded-xl text-base font-semibold transition-all duration-300 ${isActive
                    ? isScrolled
                      ? "bg-orange-50 text-orange-600 font-bold"
                      : "bg-white/20 text-white font-bold"
                    : isScrolled ? "text-gray-900 hover:bg-orange-50" : "text-white hover:bg-white/10"
                    }`}
                >
                  <div className="flex items-center space-x-3">
                    <link.icon className={`w-5 h-5 ${isActive && isScrolled ? 'text-orange-500' : ''}`} />
                    <span>{link.label}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>
    </div>
  );
}

export default Navbar;
